import { inject } from '@angular/core';
import { patchState, signalStore, signalStoreFeature, withHooks, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { Association } from '@features/associations/models/association';
import { AssociationsService } from '../associations.service';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import {
  withSelectedEntity,
  withSelectedEntities,
  setSelectedEntityId,
  setSelectedEntityIds,
} from './with-selected-entities.feature';
import { setFulfilled, setPending, withRequestStatus, withSyncToWebStorage } from '@shared/stores/store-features';
import { UsersService } from '@app/core/services/users.service';
import { ASSOCIATIONS_STORE_KEY } from './associations-store-key';
import { isCompanyOrInternalType } from '@shared/utilities/association-role.util';

/**
 * Keep an eye on this repo for extensions
 * @ngrx-traits/signals
 */

interface UserAssociationInfo {
  userRoleId: number | null;
  kmdCustomerId: number | null;
  organisationTypeId: number | null;
}

interface AssociationsStoreState {
  /**
   * Per association of the signed-in user, by association id: their role in it (`userRoleId`) and
   * its KMD customer id (`kmdCustomerId`), both from `GET /users/{id}/associations`.
   */
  userAssociations: Record<number, UserAssociationInfo>;
  /** Foreningsskuffen: `null` indtil den har været åbnet eller lukket første gang. */
  drawerOpen: boolean | null;
  /**
   * The association last opened from the drawer, so the Foreninger menu links return to it.
   * Kept apart from `selectedEntityId`, which is an association login's own association.
   */
  viewedAssociationId: number | null;
}
export const AssociationsStore = signalStore(
  { providedIn: 'root' }, // 👇 Providing `AssociationsStore` at the root level.
  withEntities<Association>(),
  withRequestStatus(),
  withSelectedEntity(), // user selected association "logged-in"
  withSelectedEntities(), // The associations added to the user
  signalStoreFeature(
    withState<AssociationsStoreState>({
      userAssociations: {},
      drawerOpen: null,
      viewedAssociationId: null,
    }),
  ),
  withSyncToWebStorage({
    key: ASSOCIATIONS_STORE_KEY,
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
    onRestore: (store) => {
      if (store.selectedEntityId() && !store.selectedEntities().find((x: Association) => x.id === store.selectedEntityId())) {
        // user does not have permission to stored association, setting to null
        patchState(store, setSelectedEntityId(null));
      }
    },
  }),

  withMethods((store) => {
    const associationsService = inject(AssociationsService);
    const usersService = inject(UsersService);

    return {
      updateAssociationInCollection(association: Association): void {
        // find the association in entities and update it
        const updatedAssociations = store.entities().map((a) => (a.id === association.id ? association : a));
        patchState(store, setAllEntities(updatedAssociations));
      },

      setDrawerOpen(open: boolean): void {
        patchState(store, { drawerOpen: open });
      },

      setViewedAssociationId(id: number): void {
        patchState(store, { viewedAssociationId: id });
      },

      setSelectedAssociation(association: Association | number): Association | null {
        const id = typeof association === 'number' ? association : association.id;
        const associationExists = store.entities().find((x) => x.id === id);

        if (!associationExists) {
          return null;
        }
        patchState(store, setSelectedEntityId(id));

        return store.selectedEntity();
      },

      /** The user's own associations, with their role in each (`GET /users/{id}/associations`). */
      setUserAssociations(userAssociations: Association[]): void {
        patchState(store, setSelectedEntityIds(userAssociations.map((a) => a.id)), {
          userAssociations: Object.fromEntries(
            userAssociations.map((a) => [
              a.id,
              {
                userRoleId: a.userRoleId ?? null,
                kmdCustomerId: a.kmdCustomerId ?? null,
                organisationTypeId: a.organisationTypeId ?? null,
              },
            ]),
          ),
        });
      },

      /** The signed-in user's role in an association of theirs, or null. */
      associationRoleId(associationId: number | null | undefined): number | null {
        return associationId ? (store.userAssociations()[associationId]?.userRoleId ?? null) : null;
      },

      /** What GET /users/{id}/associations said about the selected association, or null. */
      selectedUserAssociationInfo(): UserAssociationInfo | null {
        const id = store.selectedEntityId() as number | null;
        return id ? (store.userAssociations()[id] ?? null) : null;
      },

      /** The KMD customer id of the selected association, or null. */
      selectedAssociationKmdCustomerId(): number | null {
        return this.selectedUserAssociationInfo()?.kmdCustomerId ?? null;
      },

      /** What kind of organisation the selected association is (15, 4 or 6 — #950), or null. */
      selectedOrganisationTypeId(): number | null {
        return this.selectedUserAssociationInfo()?.organisationTypeId ?? null;
      },

      /** Is the selected association a company or an internal customer rather than an association (#950)? */
      selectedIsOrganisation(): boolean {
        return isCompanyOrInternalType(this.selectedOrganisationTypeId());
      },

      /** The signed-in user's role in the selected association, or null. */
      selectedAssociationRoleId(): number | null {
        return this.associationRoleId(store.selectedEntityId() as number | null);
      },

      clearSelectedAssociation(): void {
        patchState(store, setSelectedEntityId(null));
      },

      /** Loads all associations once; `force` re-reads a list restored from an older session. */
      async loadAssociations(force = false) {
        if (force || store.entities().length === 0) {
          const associations = await firstValueFrom(associationsService.getList());
          patchState(store, setAllEntities(associations));
        }
      },

      async loadUserAssociations() {
        // Also when a session restored from an older build has the ids but not `userAssociations`, or has them
        // without `kmdCustomerId` (before #1348 part 4 it was `customerImportId`).
        const info = Object.values(store.userAssociations());
        if (store.selectedEntities().length === 0 || info.length === 0 || info.some((i) => !('kmdCustomerId' in i))) {
          await this.refreshUserAssociations();
        }
      },

      /** Re-reads the user's associations and roles — after the user's own role changed. */
      async refreshUserAssociations() {
        const userAssociations: Association[] = await usersService.getAssociations();
        this.setUserAssociations(userAssociations ?? []);
      },

      async loadAll(): Promise<void> {
        patchState(store, setPending());

        await this.loadAssociations();
        await this.loadUserAssociations();

        patchState(store, setFulfilled());
      },
    };
  }),

  withHooks({ onInit: (store) => store.loadAll() }),
);
